import type { TopicMatch } from "@repo/app-types/entities/topic";
import {
  SlideoverDialog,
  SlideoverDialogContentGray,
  SlideoverDialogTitleHeader,
} from "@repo/ui/Slideover/slideoverDialog";
import { IconExternalLink } from "@tabler/icons-react";
import type { FC } from "react";
import {
  usePlaybookGradingInteractionMomentsQuery,
  usePlaybookGradingQuery,
} from "../../../api/entities/performancePlaybook.js";
import { appPaths } from "../../../app/appPaths.js";
import { MatchAnalysisSlideoverScaffold } from "../../transcriptViewer/matchAnalysisSlideover.js";

interface TopicMatchSlideoverDialogProps {
  match: TopicMatch;
  topicName: string;
}

function buildGradingUrl(match: TopicMatch): string {
  return appPaths.playbookCall.build({
    playbookId: match.playbookId,
    gradingId: match.gradingId,
  });
}

export const TopicMatchSlideoverDialog: FC<TopicMatchSlideoverDialogProps> = ({
  match,
  topicName,
}) => {
  const { data: moments, isLoading: isMomentsLoading } =
    usePlaybookGradingInteractionMomentsQuery({ gradingId: match.gradingId });

  const { data: grading, isLoading: isGradingLoading } =
    usePlaybookGradingQuery({ gradingId: match.gradingId });

  const isLoading = isMomentsLoading || isGradingLoading;
  const moment = moments?.find((m) => m.id === match.momentId);

  if (!moment || !grading) {
    return (
      <SlideoverDialog label={topicName}>
        <SlideoverDialogTitleHeader isLoading={isLoading}>
          {topicName}
        </SlideoverDialogTitleHeader>
        <SlideoverDialogContentGray>
          {!isLoading ? (
            <div className="p-4 text-sm text-muted-foreground">
              Match not found.
            </div>
          ) : null}
        </SlideoverDialogContentGray>
      </SlideoverDialog>
    );
  }

  const gradingUrl = buildGradingUrl(match);

  return (
    <MatchAnalysisSlideoverScaffold
      title={topicName}
      subtitle={{
        contactName: match.contactName,
        contactLinkUrl: gradingUrl,
        recordedAt: match.recordedAt,
      }}
      rightAction={
        <a
          href={gradingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-500"
        >
          View grading
          <IconExternalLink className="size-3.5" />
        </a>
      }
      teammateName={match.teammateName}
      summaryText={moment.data.description}
      analysis={moment.data.analysis}
      gradingId={match.gradingId}
      recordings={grading.recordings}
    />
  );
};
